import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { canEdit, canManage } from "@/lib/constants";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Plus, Upload, FileText, Link as LinkIcon, StickyNote, Trash2, Download, CheckSquare, Square, Link2 } from "lucide-react";

const emptyEv = { theme_key: "", title: "", description: "", source_type: "note", url: "", file_id: null, tags: [] };
const iconFor = { document: FileText, link: LinkIcon, note: StickyNote };

export default function Evidence() {
  const { user } = useAuth();
  const [themes, setThemes] = useState([]);
  const [items, setItems] = useState([]);
  const [actions, setActions] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyEv);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tagStr, setTagStr] = useState("");
  const [pickedActions, setPickedActions] = useState(new Set());
  const [linkOpen, setLinkOpen] = useState(false);
  const [linking, setLinking] = useState(null);
  const fileRef = useRef();

  const load = async () => {
    const [t, e, a] = await Promise.all([api.get("/themes"), api.get("/evidence"), api.get("/actions")]);
    setThemes(t.data); setItems(e.data); setActions(a.data);
    if (!form.theme_key && t.data.length) setForm((f) => ({ ...f, theme_key: t.data[0].key }));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const filtered = useMemo(() => items.filter((e) => filter === "all" || e.theme_key === filter), [items, filter]);

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/evidence/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, file_id: data.file_id, source_type: "document", title: f.title || data.filename }));
      toast.success("File uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const tags = tagStr.split(",").map((s) => s.trim()).filter(Boolean);
      await api.post("/evidence", { ...form, tags, action_ids: Array.from(pickedActions) });
      toast.success("Evidence added");
      setOpen(false); setForm({ ...emptyEv, theme_key: form.theme_key }); setTagStr(""); setPickedActions(new Set());
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const openLinker = (ev) => { setLinking(ev); setPickedActions(new Set(ev.action_ids || [])); setLinkOpen(true); };
  const saveLinks = async () => {
    try {
      await api.patch(`/evidence/${linking.id}`, { action_ids: Array.from(pickedActions) });
      toast.success("Links saved"); setLinkOpen(false); load();
    } catch (e) { toast.error("Failed"); }
  };

  const toggleAction = (id) => { const s = new Set(pickedActions); s.has(id) ? s.delete(id) : s.add(id); setPickedActions(s); };

  const del = async (id) => {
    if (!confirm("Delete evidence?")) return;
    await api.delete(`/evidence/${id}`); load(); toast.success("Deleted");
  };

  const download = (fileId, filename) => {
    const token = localStorage.getItem("qip_token");
    const url = `${api.defaults.baseURL}/evidence/files/${fileId}/download?auth=${token}`;
    window.open(url, "_blank");
  };

  const themeTitle = (k) => themes.find((t) => t.key === k)?.title || k;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-indigo-800">Evidence library</div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 mt-1" style={{ fontFamily: "Plus Jakarta Sans" }}>Every claim, backed by evidence</h1>
          <p className="text-slate-600 mt-1">Upload policies, minutes, learner work and data. Tag by theme to power your SAR.</p>
        </div>
        {canEdit(user?.role) && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-evidence-btn" className="bg-indigo-900 hover:bg-indigo-950 text-white"><Plus className="h-4 w-4 mr-1.5" />Add evidence</Button></DialogTrigger>
            <DialogContent className="bg-white max-w-lg">
              <DialogHeader><DialogTitle>New evidence item</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Theme</Label>
                  <Select value={form.theme_key} onValueChange={(v) => setForm({ ...form, theme_key: v })}>
                    <SelectTrigger data-testid="ev-theme"><SelectValue /></SelectTrigger>
                    <SelectContent>{themes.map((t) => <SelectItem key={t.key} value={t.key}>{t.title}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Type</Label>
                  <Select value={form.source_type} onValueChange={(v) => setForm({ ...form, source_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="document">Document upload</SelectItem><SelectItem value="link">External link</SelectItem><SelectItem value="note">Note</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Title</Label><Input data-testid="ev-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
                <div><Label>Description</Label><Textarea data-testid="ev-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                {form.source_type === "link" && <div><Label>URL</Label><Input value={form.url || ""} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…" /></div>}
                {form.source_type === "document" && (
                  <div>
                    <Label>File</Label>
                    <input ref={fileRef} type="file" data-testid="ev-file-input" className="hidden" onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])} />
                    <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full">
                      <Upload className="h-4 w-4 mr-2" />{uploading ? "Uploading…" : form.file_id ? "File attached — replace" : "Choose file (PDF, docx, xlsx, image)"}
                    </Button>
                    {form.file_id && <div className="text-xs text-emerald-700 mt-1">File attached</div>}
                  </div>
                )}
                <div><Label>Tags (comma separated)</Label><Input value={tagStr} onChange={(e) => setTagStr(e.target.value)} placeholder="policy, board, cycle-2" /></div>
                <div>
                  <Label>Link to QIP actions ({pickedActions.size} selected)</Label>
                  <div className="mt-1 max-h-32 overflow-auto rounded-md border border-slate-200 divide-y divide-slate-100">
                    {actions.filter((a) => a.theme_key === form.theme_key).map((a) => {
                      const on = pickedActions.has(a.id);
                      return (
                        <button data-testid={`link-act-${a.id}`} type="button" key={a.id} onClick={() => toggleAction(a.id)} className={`w-full text-left px-3 py-2 text-xs flex items-start gap-2 ${on ? "bg-indigo-50" : "hover:bg-slate-50"}`}>
                          {on ? <CheckSquare className="h-3.5 w-3.5 text-indigo-800 mt-0.5" /> : <Square className="h-3.5 w-3.5 text-slate-400 mt-0.5" />}
                          <span className="font-medium text-slate-900">{a.title}</span>
                        </button>
                      );
                    })}
                    {actions.filter((a) => a.theme_key === form.theme_key).length === 0 && <div className="px-3 py-2 text-xs text-slate-500">No actions on this theme yet.</div>}
                  </div>
                </div>
              </div>
              <DialogFooter><Button data-testid="save-evidence-btn" onClick={submit} disabled={busy || !form.title || !form.theme_key} className="bg-indigo-900 hover:bg-indigo-950 text-white">{busy ? "Saving…" : "Add evidence"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="p-4 flex items-center gap-3 flex-wrap border-slate-200">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="ev-filter" className="w-56"><SelectValue placeholder="All themes" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All themes</SelectItem>{themes.map((t) => <SelectItem key={t.key} value={t.key}>{t.title}</SelectItem>)}</SelectContent>
        </Select>
        <div className="text-xs text-slate-500 ml-auto font-mono">{filtered.length} items</div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.length === 0 && <Card className="p-8 text-center text-slate-500 md:col-span-2 xl:col-span-3">No evidence yet.</Card>}
        {filtered.map((e) => {
          const Icon = iconFor[e.source_type] || FileText;
          return (
            <Card key={e.id} data-testid={`evidence-${e.id}`} className="p-4 border-slate-200 hover:shadow-md transition-shadow duration-200">
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-md bg-indigo-50 text-indigo-800 grid place-items-center"><Icon className="h-4 w-4" /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{themeTitle(e.theme_key)}</div>
                  <div className="font-semibold text-slate-900 truncate">{e.title}</div>
                  {e.description && <div className="text-xs text-slate-600 mt-1 line-clamp-2">{e.description}</div>}
                  <div className="mt-2 flex flex-wrap gap-1">{(e.tags || []).map((t) => <Badge key={t} variant="outline" className="text-[10px] border-slate-300">{t}</Badge>)}</div>
                  <div className="mt-3 flex items-center gap-3 text-xs text-slate-500">
                    <span>By {e.created_by_name || "—"}</span>
                    {(e.action_ids || []).length > 0 && <span className="inline-flex items-center gap-1 text-emerald-800"><Link2 className="h-3 w-3" />{(e.action_ids || []).length} action{(e.action_ids || []).length === 1 ? "" : "s"}</span>}
                    {e.file && <button onClick={() => download(e.file_id, e.file.filename)} className="inline-flex items-center gap-1 text-indigo-800 hover:underline"><Download className="h-3 w-3" />{e.file.filename}</button>}
                    {e.url && <a href={e.url} target="_blank" rel="noreferrer" className="text-indigo-800 hover:underline">Open link</a>}
                    <button data-testid={`link-btn-${e.id}`} onClick={() => openLinker(e)} className="ml-auto text-indigo-800 hover:underline">Link actions</button>
                    {canManage(user?.role) && <button data-testid={`del-ev-${e.id}`} onClick={() => del(e.id)} className="text-slate-400 hover:text-rose-700"><Trash2 className="h-3.5 w-3.5" /></button>}
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle>Link evidence to QIP actions</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 mb-2">"{linking?.title}" · {linking?.theme_key}</p>
          <div className="max-h-72 overflow-auto rounded-md border border-slate-200 divide-y divide-slate-100">
            {actions.filter((a) => a.theme_key === linking?.theme_key).map((a) => {
              const on = pickedActions.has(a.id);
              return (
                <button data-testid={`linker-${a.id}`} key={a.id} type="button" onClick={() => toggleAction(a.id)} className={`w-full text-left px-3 py-2 text-sm flex items-start gap-2 ${on ? "bg-indigo-50" : "hover:bg-slate-50"}`}>
                  {on ? <CheckSquare className="h-4 w-4 text-indigo-800 mt-0.5" /> : <Square className="h-4 w-4 text-slate-400 mt-0.5" />}
                  <div><div className="font-medium text-slate-900">{a.title}</div><div className="text-xs text-slate-500">{a.owner || "Unassigned"} · {a.progress || 0}%</div></div>
                </button>
              );
            })}
            {actions.filter((a) => a.theme_key === linking?.theme_key).length === 0 && <div className="px-3 py-4 text-sm text-slate-500">No actions on this theme.</div>}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setLinkOpen(false)}>Cancel</Button><Button data-testid="save-links-btn" onClick={saveLinks} className="bg-indigo-900 hover:bg-indigo-950 text-white">Save links</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
